import React, { useState, useEffect, useRef } from "react";
import { ResponsiveTreeMap } from "@nivo/treemap";
import { ExtendedChartReportProps } from "./visualizations/VisualizationProps";
import { getEnrichmentData, sendDataToRevigo } from "../api/encrichment";
import {
  createEnrichmentDataFromArray,
  createEnrichmentDataFromOtherArrays,
  filterDataAccordingToFilters,
  getActiveListMinMaxValue,
  shuffleArray,
} from "./util/EnrichmentUtil";
import { useSelector } from "react-redux";
import { applicationGetConnection } from "../application/ApplicationSelectors";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Button, Stack, useTheme, Divider, Chip, Paper } from "@mui/material";
import { Typography } from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import SendIcon from "@mui/icons-material/Send";
import TuneIcon from "@mui/icons-material/Tune";

const ENRICHEMENT_TOPICS = ["Enrichment", "MF", "BP", "CC"];
const NUMBER_KEYS = ["mpat", "mt", "npat", "nt", "padj", "pval", "m", "n"];

const legendNumber = 6;

export default function EnrichmentChart(props: ExtendedChartReportProps) {
  const { settings } = props;
  const { enableTable } = settings;
  const dashboardState = useSelector(
    (state: any) => state?.dashboard?.settings?.parameters
  );
  const connection = useSelector(applicationGetConnection);
  const theme = useTheme();
  const [tableColumns, setTableColumns] = useState([]);
  const geneList = dashboardState?.neodash_genelist_name;
  const database = connection?.database;

  const [enrichmentData, setEnrichmentData] = useState<any>(null);
  const [selectedTarget, setSelectedTarget] = useState("go");
  const [responseData, setResponseData] = useState<any>(null);
  const [cellValueType, setCellValueType] = useState("formattedValue");
  const [filters, setFilters] = useState([]);
  const [activeListMinMax, setActiveListMinMax] = useState({ min: 0, max: 0 });
  const [activeLegent, setActiveLegend] = useState([]);

  const handleCellValueType = (event, newType) => {
    setCellValueType(newType);
  };

  const calculateLegend = () => {
    const stepSize =
      (activeListMinMax.max - activeListMinMax.min) / legendNumber;
    if (stepSize === 0) {
      return;
    }
    const legend: any = [];
    for (let i = 0; i < legendNumber; i++) {
      legend.push(Math.ceil(activeListMinMax.min + (i + 1) * stepSize));
    }
    setActiveLegend(legend);
  };

  useEffect(() => {
    calculateLegend();
  }, [activeListMinMax]);

  const sendToRevigo = async () => {
    let tempString = "#PlantGenIE Enrichment Results \n";
    responseData.go.forEach((element) => {
      tempString += `${element.id} ${element.pval.toPrecision(4)} \n`;
    });
    try {
      const response = await sendDataToRevigo(tempString);
      if (response.status === 200) {
        window.open(response.data);
      }
    } catch (error) {
      alert("Error occured while sending data to Revigo");
    }
  };

  const downloadImageAsSVG = () => {
    const fileName = prompt("Please enter file name", "enrichment");
    if (!fileName) {
      alert("Invalid filename");
      return;
    }
    const element = document.getElementById("chart-container");
    let serializer = new XMLSerializer();
    let source = serializer.serializeToString(
      element!.getElementsByTagName("svg")[0]
    );
    if (!source.match(/^<svg[^>]+xmlns="http\:\/\/www\.w3\.org\/2000\/svg"/)) {
      source = source.replace(
        /^<svg/,
        '<svg xmlns="http://www.w3.org/2000/svg"'
      );
    }
    if (!source.match(/^<svg[^>]+"http\:\/\/www\.w3\.org\/1999\/xlink"/)) {
      source = source.replace(
        /^<svg/,
        '<svg xmlns:xlink="http://www.w3.org/1999/xlink"'
      );
    }
    source = '<?xml version="1.0" standalone="no"?>\r\n' + source;
    let url = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(source);
    let link = document.createElement("a");

    link.download = fileName + ".svg";
    link.href = url;
    link.click();
  };

  const initializeTableColumns = (keys) => {
    const columns = keys.map((key) => {
      if (NUMBER_KEYS.includes(key)) {
        return { field: key, headerName: key, width: 200, type: "number" };
      }
      return { field: key, headerName: key, width: 200, filterable: false };
    });
    setTableColumns(columns);
  };

  const fetchData = async () => {
    try {
      const response = await getEnrichmentData(database, geneList);
      if (response.status === 200) {
        initializeTableColumns(Object.keys(response.data.go[0]));
        setResponseData(response.data);
        getActiveListMinMaxValue(response.data.go, setActiveListMinMax);
        const processedData = createEnrichmentDataFromArray(
          response?.data?.go,
          geneList.length
        );
        setEnrichmentData(processedData);
      }
    } catch (error) {
      alert("Error occured");
    }
  };

  useEffect(() => {
    fetchData();
  }, [geneList]);

  const getColorOfNode = (node) => {
    const value = node?.data?.loc;
    if (!value) {
      return "#fff";
    }
    if (value <= activeLegent[0]) {
      return "#FFF9E6";
    } else if (value <= activeLegent[1]) {
      return "#FFEB99";
    } else if (value <= activeLegent[2]) {
      return "#FFCC66";
    } else if (value <= activeLegent[3]) {
      return "#FF9933";
    } else if (value <= activeLegent[4]) {
      return "#FF6600";
    } else if (value <= activeLegent[5]) {
      return "#CC3300";
    } else {
      return "transparent";
    }
  };

  const handleChange = (event) => {
    setSelectedTarget(event.target.value);
    initializeTableColumns(Object.keys(responseData[event.target.value][0]));
    if (event.target.value === "kegg") {
      getActiveListMinMaxValue(responseData.kegg, setActiveListMinMax);
      const processedData = createEnrichmentDataFromOtherArrays(
        responseData.kegg,
        geneList.length
      );
      setEnrichmentData(processedData);
    } else if (event.target.value === "go") {
      getActiveListMinMaxValue(responseData.go, setActiveListMinMax);
      const processedData = createEnrichmentDataFromArray(
        responseData.go,
        geneList.length
      );
      setEnrichmentData(processedData);
    } else if (event.target.value === "pfam") {
      getActiveListMinMaxValue(responseData.pfam, setActiveListMinMax);
      const processedData = createEnrichmentDataFromArray(
        responseData.pfam,
        geneList.length
      );
      setEnrichmentData(processedData);
    }
  };

  const rollTheDice = () => {
    if (!enrichmentData?.children) return;
    const enrichmentChildren = enrichmentData?.children;

    for (const element of enrichmentChildren) {
      const newChild = element;
      newChild.children = shuffleArray(newChild.children);
    }
    const newEnrichmentData = {
      ...enrichmentData,
      children: enrichmentChildren,
    };
    setEnrichmentData(newEnrichmentData);
  };

  const handleChangeEnrichmentData = (newData) => {
    if (selectedTarget === "kegg") {
      const processedData = createEnrichmentDataFromOtherArrays(
        newData,
        geneList.length
      );
      setEnrichmentData(processedData);
    } else if (selectedTarget === "go" || selectedTarget === "pfam") {
      const processedData = createEnrichmentDataFromArray(
        newData,
        geneList.length
      );
      setEnrichmentData(processedData);
    }
  };

  const handleChangeFilters = (model) => {
    if (model?.items && model?.items?.length > 0) {
      setFilters(model.items);
      const newData = filterDataAccordingToFilters(
        responseData[selectedTarget],
        model.items
      );
      handleChangeEnrichmentData(newData);
    } else {
      setFilters([]);
      handleChangeEnrichmentData(responseData[selectedTarget]);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* TreeMap Visualization - Top Section */}
      {enrichmentData && activeLegent.length > 0 && (
        <Box sx={{ bgcolor: 'white', p: 2 }}>
          <div
            id="chart-container"
            style={{ height: enableTable ? 400 : 600 }}
          >
            <ResponsiveTreeMap
              data={enrichmentData}
              identity="name"
              value="loc"
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              labelSkipSize={12}
              colors={getColorOfNode}
              label={(e) => {
                if (ENRICHEMENT_TOPICS.includes(e.id)) {
                  return e.id;
                } else {
                  return e[cellValueType];
                }
              }}
              labelTextColor={{
                from: "color",
                modifiers: [["darker", 1.2]],
              }}
              parentLabelPosition="left"
              parentLabelTextColor={{
                from: "color",
                modifiers: [["darker", 2]],
              }}
              borderColor={{
                from: "color",
                modifiers: [["darker", 0.1]],
              }}
            />
          </div>

          {/* Legend */}
          <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
            <Stack direction="row" sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
              {[
                { color: '#FFF9E6', value: activeLegent[0] },
                { color: '#FFEB99', value: activeLegent[1] },
                { color: '#FFCC66', value: activeLegent[2] },
                { color: '#FF9933', value: activeLegent[3] },
                { color: '#FF6600', value: activeLegent[4] },
                { color: '#CC3300', value: activeLegent[5] },
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 70,
                    height: 32,
                    backgroundColor: item.color,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRight: index < 5 ? '1px solid #e0e0e0' : 'none',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {`< ${item.value}`}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Box>
      )}

      {/* Control Panel - Bottom Section */}
      {responseData && (
        <Box sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #e0e0e0' }}>
          <Stack
            direction="row"
            spacing={2}
            sx={{ mb: 3, height: 100 }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TuneIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Enrichment Controls
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={downloadImageAsSVG}
                sx={{ textTransform: 'none' }}
              >
                Export SVG
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SendIcon />}
                onClick={sendToRevigo}
                sx={{ textTransform: 'none' }}
              >
                Send to Revigo
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          <Stack spacing={3}>
            {/* Domain Selection & Display Options */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#555' }}>
                Data Source & Display
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <FormControl sx={{ minWidth: 180 }} size="small">
                  <InputLabel>Domain</InputLabel>
                  <Select
                    value={selectedTarget}
                    label="Domain"
                    onChange={handleChange}
                  >
                    <MenuItem value="go">Gene Ontology</MenuItem>
                    <MenuItem value="kegg">KEGG</MenuItem>
                    <MenuItem value="pfam">Pfam</MenuItem>
                  </Select>
                </FormControl>

                <ToggleButtonGroup
                  value={cellValueType}
                  exclusive
                  onChange={handleCellValueType}
                  size="small"
                >
                  <ToggleButton value="id" sx={{ textTransform: 'none', px: 2 }}>
                    Name
                  </ToggleButton>
                  <ToggleButton value="formattedValue" sx={{ textTransform: 'none', px: 2 }}>
                    Value
                  </ToggleButton>
                </ToggleButtonGroup>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ShuffleIcon />}
                  onClick={rollTheDice}
                  sx={{ textTransform: 'none' }}
                >
                  Rearrange Tree
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Box>
      )}

      {/* Data Table Section */}
      {enableTable && responseData && tableColumns && (
        <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#555' }}>
            Detailed Data
          </Typography>
          <div style={{ height: 500 }}>
            <DataGrid
              rows={responseData[selectedTarget]}
              columns={tableColumns}
              components={{ Toolbar: GridToolbar }}
              onFilterModelChange={handleChangeFilters}
              initialState={{
                pagination: {
                  page: 0,
                  pageSize: 20,
                },
              }}
            />
          </div>
        </Box>
      )}
    </Box>
  );
}